/**
 * Raid Rewards View
 *
 * Displays raid completion results and rewards.
 * Features:
 * - Victory/Defeat outcome display
 * - Rewards breakdown (coins, XP, items)
 * - Pokemon recruitment results (if any)
 * - Return to home button
 */

import {
  useRaid,
  CoopRaidState,
  RaidLobbyPlayer,
} from "@/providers/raid-provider";
import { StrokedText } from "@/components/ui/text/stroked-text";
import { Button } from "@/components/ui/buttons/button";
import { PokemonSprite } from "@/components/pokemon/pokemon-sprite";
import { AnimatedVFX } from "@/components/vfx/animated-vfx";
import { Direction } from "types/battle-engine/battle";
import classNames from "classnames";
import { useSPAContext } from "@/providers/spa-provider";
import { useState } from "react";
import { getRaidDisplayPokemons } from "@/utils/raid-display-utils";

interface RaidRewardsOverlayProps {
  raidState?: CoopRaidState;
}

/**
 * Overlay component for unified Raid route
 */
export const RaidRewardsOverlay = ({ raidState }: RaidRewardsOverlayProps) => {
  const { setCurrentRoute } = useSPAContext();
  const contextRaid = useRaid();
  const state = raidState || contextRaid.raidState;
  const result = state.result;

  const isVictory = result?.outcome === "victory";
  const isDefeat = result?.outcome === "defeat";
  const isAbandoned = result?.outcome === "abandoned";

  const [loadingHome, setLoadingHome] = useState(false);

  const handleReturnHome = () => {
    setLoadingHome(true);
    setTimeout(() => {
      setCurrentRoute(undefined);
      window.location.reload();
    }, 1500);
  };

  // Get one pokemon from each player to display
  const displayPokemons = getRaidDisplayPokemons(state.players);

  return (
    <div className="flex flex-col h-full w-full bg-pk-gray-1/95 overflow-hidden relative">
      {/* Loading Overlay */}
      {loadingHome && (
        <div className="absolute inset-0 z-50 bg-black/80 flex flex-col items-center justify-center gap-4 animate-in fade-in duration-300">
          <div className="w-12 h-12 border-4 border-pk-blue-4 border-t-transparent rounded-full animate-spin" />
          <StrokedText
            fontSize={24}
            strokeWidth={4}
            strokeColor="#000"
            fillColor="#fff"
          >
            Returning to Home...
          </StrokedText>
        </div>
      )}

      {/* Header */}
      <div
        className={classNames(
          "p-6 text-center border-b-4 transition-all duration-500",
          isVictory
            ? "bg-pk-green-2/30 border-pk-green-4"
            : isAbandoned
            ? "bg-pk-gray-2 border-pk-gray-3"
            : "bg-pk-red-3/30 border-pk-red-4",
        )}
      >
        <StrokedText
          fontSize={32}
          strokeWidth={4}
          strokeColor="#232020"
          fillColor={
            isVictory ? "#4ade80" : isAbandoned ? "#9ca3af" : "#f87171"
          }
        >
          {isVictory ? "VICTORY!" : isAbandoned ? "ABANDONED" : "DEFEAT"}
        </StrokedText>
        {isDefeat && result?.defeat_round !== undefined && (
          <p className="text-pk-gray-4 text-sm mt-2">
            Defeated at Room {result.defeat_round + 1}
          </p>
        )}
        {isAbandoned && result?.quitter_name && (
          <p className="text-pk-gray-4 text-sm mt-2">
            {result.quitter_name} left the raid
          </p>
        )}
      </div>

      {/* Cinematic Display */}
      <div className="h-48 relative flex items-center justify-center bg-black/20 my-2 overflow-hidden shrink-0">
        <div className="absolute inset-0 opacity-30 bg-[url('/assets/app-ui/backgrounds/forest.png')] bg-cover bg-center" />

        <div className="relative z-10 flex items-end gap-6 pb-4">
          {displayPokemons.map((p, idx) => (
            <div key={idx} className="flex flex-col items-center">
              <div className="relative">
                <PokemonSprite
                  pokemon={{
                    base_pokemon_name: p.base_pokemon_name || "",
                  }}
                  className={classNames(
                    "scale-[2.5] filter drop-shadow-lg transition-all duration-500",
                    {
                      "grayscale brightness-75": isDefeat || isAbandoned,
                    },
                  )}
                  direction={Direction.S}
                  animation={isVictory ? "Attack" : "Sleep"} // Using Sleep as "fainted" approx if needed, or Idle
                  showShadow={true}
                />
                {/* Victory Effects */}
                {isVictory && (
                  <div className="absolute inset-0 -top-8 left-0">
                    <AnimatedVFX
                      id={`victory-${idx}`}
                      spriteUrl="/assets/spells/vfx/stars_effect_000__40.png"
                      loops={0} // Loop infinitely
                      speed={0.5}
                      spriteScale={1}
                      position={{ x: 0, y: 0 }}
                    />
                  </div>
                )}
                {/* Defeat Effects (maybe Zzz) */}
                {(isDefeat || isAbandoned) && (
                  <div className="absolute -top-4 right-0 animate-pulse text-white text-xs font-bold">
                    Zzz...
                  </div>
                )}
              </div>
              <span className="mt-2 text-[10px] text-white/70 bg-black/40 px-2 rounded-full">
                {p.username}
              </span>
            </div>
          ))}
        </div>

        {isVictory && (
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
            <div className="animate-ping absolute inline-flex h-full w-full rounded-full bg-pk-green-4/10 opacity-75"></div>
          </div>
        )}
      </div>

      {/* Rewards Section */}
      <div className="flex-1 p-4 overflow-y-auto">
        <div className="max-w-md mx-auto space-y-4">
          {/* Coins & XP */}
          {result && (
            <div className="bg-pk-gray-2 rounded-xl p-4 border-2 border-pk-gray-3">
              <h3 className="text-pk-gray-4 text-xs font-bold uppercase tracking-wider mb-3">
                Rewards Earned
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <img
                    src="/assets/app-ui/poke-coin.png"
                    alt="Coins"
                    className="w-10 h-10"
                  />
                  <div>
                    <p className="text-pk-yellow-3 font-bold text-xl">
                      +{result.coins_earned}
                    </p>
                    <p className="text-pk-gray-4 text-xs">Coins</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-pk-blue-3 rounded-full flex items-center justify-center">
                    <span className="text-xl">⭐</span>
                  </div>
                  <div>
                    <p className="text-pk-blue-4 font-bold text-xl">
                      +{result.experience_earned}
                    </p>
                    <p className="text-pk-gray-4 text-xs">XP</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Items */}
          {result?.rewards?.items && result.rewards.items.length > 0 && (
            <div className="bg-pk-gray-2 rounded-xl p-4 border-2 border-pk-gray-3">
              <h3 className="text-pk-gray-4 text-xs font-bold uppercase tracking-wider mb-3">
                Items Found
              </h3>
              <div className="flex flex-wrap gap-2">
                {result.rewards.items.map((item, index) => (
                  <div
                    key={index}
                    className="bg-pk-gray-1 px-3 py-2 rounded-lg border border-pk-gray-3"
                  >
                    <span className="text-pk-gray-5 text-sm font-bold">
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pokemon Recruited */}
          {result?.rewards?.recruitment_pool &&
            result.rewards.recruitment_pool.length > 0 && (
              <div className="bg-pk-gray-2 rounded-xl p-4 border-2 border-pk-green-4">
                <h3 className="text-pk-green-4 text-xs font-bold uppercase tracking-wider mb-3">
                  Pokemon Recruited!
                </h3>
                <div className="flex flex-wrap gap-2">
                  {result.rewards.recruitment_pool.map((pokemon, index) => (
                    <div
                      key={index}
                      className="bg-pk-green-2/30 px-3 py-2 rounded-lg border border-pk-green-4"
                    >
                      <span className="text-pk-green-4 text-sm font-bold">
                        {pokemon}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

          {/* Boss Drops */}
          {result?.boss_drops && result.boss_drops.length > 0 && (
            <div className="bg-pk-gray-2 rounded-xl p-4 border-2 border-pk-purple-4">
              <h3 className="text-pk-purple-4 text-xs font-bold uppercase tracking-wider mb-3">
                Boss Drops
              </h3>
              <div className="flex flex-wrap gap-2">
                {result.boss_drops.map((drop, index) => (
                  <div
                    key={index}
                    className="bg-pk-purple-3/30 px-3 py-2 rounded-lg border border-pk-purple-4"
                  >
                    <span className="text-pk-purple-4 text-sm font-bold">
                      {drop}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No rewards message */}
          {!result && (
            <div className="text-center py-8">
              <p className="text-pk-gray-4 text-lg">Calculating rewards...</p>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="p-4 bg-pk-gray-2 border-t-4 border-pk-gray-1">
        <Button
          variant="blue"
          onClick={handleReturnHome}
          className="w-full"
          disabled={loadingHome}
        >
          <span className="text-lg font-bold uppercase tracking-wide">
            {loadingHome ? "Loading..." : "Return to Home"}
          </span>
        </Button>
      </div>
    </div>
  );
};

export default RaidRewardsOverlay;
